import { useState } from 'react';
import { CheckCircle, XCircle, Heart, Flame } from 'lucide-react';
import { motion } from 'framer-motion';
import { useLanguage } from '../contexts/LanguageContext';

/**
 * Modül 3.7.2: Duolingo tarzı İngilizce mülakat oynatıcısı.
 *
 * Props:
 *   session: backend'den dönen oturum dokümanı (questions, current_index, lives, streak,
 *            total_score, max_score, status, answers)
 *   onAnswer({ question_id, answer_text, answer_type }): cevap POST eder, backend sonucunu döner
 *   onComplete(): tamamlandığında çağrılır (Modules'a dönüş)
 *   lang: 'tr' | 'en'  — (legacy prop, kept for compatibility; t() uses LanguageContext)
 */
export default function EnglishInterviewPlayer({ session, onAnswer, onComplete, lang }) {
  const { t } = useLanguage();
  const [selectedChoice, setSelectedChoice] = useState(null);
  const [typedAnswer, setTypedAnswer] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [lastResult, setLastResult] = useState(null);

  const totalQuestions = session?.questions?.length || 0;
  const currentQuestion = session?.questions?.[session.current_index];
  const isEnded = session?.status === 'completed' || session?.status === 'failed';

  if (isEnded) {
    return <CompletionScreen session={session} onComplete={onComplete} lang={lang} />;
  }

  if (!currentQuestion) return null;

  const resetAnswerInputs = () => {
    setSelectedChoice(null);
    setTypedAnswer('');
  };

  const submit = async () => {
    let answerText = '';
    if (currentQuestion.answer_type === 'choice') {
      if (!selectedChoice) return;
      answerText = selectedChoice;
    } else {
      if (typedAnswer.trim().length < 5) return;
      answerText = typedAnswer.trim();
    }

    setSubmitting(true);
    try {
      const result = await onAnswer({
        question_id: currentQuestion.id,
        answer_text: answerText,
        answer_type: currentQuestion.answer_type,
      });
      if (result) setLastResult(result);
    } finally {
      setSubmitting(false);
    }
  };

  const nextQuestion = () => {
    setLastResult(null);
    resetAnswerInputs();
  };

  const wordCount = typedAnswer.trim().split(/\s+/).filter(Boolean).length;
  const charCount = typedAnswer.trim().length;
  const submitDisabled =
    submitting ||
    (currentQuestion.answer_type === 'choice' ? !selectedChoice : charCount < 5);

  return (
    <div
      className="bg-white rounded-2xl p-6 border-2 border-gray-200"
      data-testid="english-interview-player"
    >
      {/* Ust bar: can + ilerleme + streak */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex gap-1" data-testid="ei-lives">
          {[...Array(5)].map((_, i) => (
            <Heart
              key={i}
              size={18}
              className={i < session.lives ? 'text-red-500 fill-red-500' : 'text-gray-300'}
            />
          ))}
        </div>
        <div className="text-sm font-bold" data-testid="ei-progress">
          {(t('ei.question_counter') || 'Soru {current} / {total}')
            .replace('{current}', Math.min(session.current_index + 1, totalQuestions))
            .replace('{total}', totalQuestions)}
        </div>
        {session.streak > 0 ? (
          <div className="flex items-center gap-1 text-sm font-bold text-orange-600" data-testid="ei-streak">
            <Flame size={14} /> {session.streak}
          </div>
        ) : (
          <div className="w-10" />
        )}
      </div>

      {/* Soru */}
      <h2 className="text-lg font-bold mb-1" data-testid="ei-question">
        {currentQuestion.text}
      </h2>
      {currentQuestion.context_tr && (
        <p className="text-xs text-[var(--text-muted)] mb-4">{currentQuestion.context_tr}</p>
      )}

      {!lastResult ? (
        <>
          {currentQuestion.answer_type === 'choice' ? (
            <div className="space-y-2 mb-4">
              {(currentQuestion.options || []).map((opt, i) => (
                <button
                  key={i}
                  onClick={() => setSelectedChoice(opt)}
                  className={`w-full p-3 rounded-xl border-2 text-left transition ${
                    selectedChoice === opt
                      ? 'border-blue-500 bg-blue-50'
                      : 'border-gray-300 hover:border-gray-400'
                  }`}
                  data-testid={`ei-option-${i}`}
                >
                  {opt}
                </button>
              ))}
            </div>
          ) : (
            <div className="mb-4">
              <textarea
                value={typedAnswer}
                onChange={(e) => setTypedAnswer(e.target.value)}
                placeholder={t('ei.textarea_placeholder') || 'Type your English answer...'}
                className="w-full p-3 rounded-xl border-2 border-gray-300 focus:border-blue-500 outline-none min-h-[100px] text-sm"
                data-testid="ei-typed-input"
              />
              <div className="text-xs text-[var(--text-muted)] mt-1">
                {(t('ei.word_count') || '{count} words').replace('{count}', wordCount)}
                {' · '}
                {(t('ei.char_count') || '{count}/5 chars').replace('{count}', charCount)}
              </div>
            </div>
          )}

          <button
            onClick={submit}
            disabled={submitDisabled}
            className="w-full py-3 bg-blue-500 text-white rounded-xl font-bold disabled:opacity-50"
            data-testid="ei-submit-btn"
          >
            {submitting ? '...' : (t('ei.submit_btn') || 'Submit')}
          </button>
        </>
      ) : (
        <ResultPanel result={lastResult} onNext={nextQuestion} lang={lang} />
      )}
    </div>
  );
}

function ResultPanel({ result, onNext, lang }) {
  const { t } = useLanguage();
  const { evaluation = {}, is_correct, points_earned, session_status, is_last_question } =
    result || {};

  const feedback =
    evaluation[`feedback_${lang}`] || evaluation.feedback_en || evaluation.feedback_tr || '';
  const hasLlmScores = evaluation.grammar_score !== undefined;
  const ended = session_status === 'completed' || session_status === 'failed';

  return (
    <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} data-testid="ei-result-panel">
      <div
        className={`p-4 rounded-xl border-2 mb-3 ${
          is_correct ? 'bg-green-50 border-green-300' : 'bg-red-50 border-red-300'
        }`}
      >
        <div className="flex items-center gap-2 mb-2">
          {is_correct ? (
            <CheckCircle className="text-green-600" size={20} />
          ) : (
            <XCircle className="text-red-600" size={20} />
          )}
          <span className="font-bold">
            {is_correct
              ? (t('ei.result.correct') || 'Well done!')
              : (t('ei.result.incorrect') || 'Room for improvement')}
          </span>
          <span className="ml-auto text-sm font-bold" data-testid="ei-xp-earned">
            +{points_earned || 0} XP
          </span>
        </div>
        {feedback && <p className="text-sm">{feedback}</p>}
      </div>

      {hasLlmScores && (
        <div className="grid grid-cols-3 gap-2 mb-3 text-xs">
          <div className="bg-gray-50 p-2 rounded-lg text-center">
            <div className="font-bold text-blue-700">{evaluation.grammar_score}/10</div>
            <div className="text-[10px] text-[var(--text-muted)]">
              {t('ei.result.grammar') || 'Grammar'}
            </div>
          </div>
          <div className="bg-gray-50 p-2 rounded-lg text-center">
            <div className="font-bold text-purple-700">{evaluation.relevance_score}/10</div>
            <div className="text-[10px] text-[var(--text-muted)]">
              {t('ei.result.relevance') || 'Relevance'}
            </div>
          </div>
          <div className="bg-gray-50 p-2 rounded-lg text-center">
            <div className="font-bold text-pink-700">{evaluation.tone_score}/10</div>
            <div className="text-[10px] text-[var(--text-muted)]">
              {t('ei.result.tone') || 'Tone'}
            </div>
          </div>
        </div>
      )}

      {evaluation.better_version && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 mb-3" data-testid="ei-better-version">
          <div className="text-[10px] font-bold text-blue-700 mb-1">
            {t('ei.result.better_version_label') || 'IMPROVED VERSION'}
          </div>
          <p className="text-sm italic">&ldquo;{evaluation.better_version}&rdquo;</p>
        </div>
      )}

      <button
        onClick={onNext}
        className="w-full py-3 bg-green-500 text-white rounded-xl font-bold"
        data-testid="ei-next-btn"
      >
        {ended || is_last_question
          ? (t('ei.see_results') || 'See Results')
          : (t('ei.next_question') || 'Next Question')}
      </button>
    </motion.div>
  );
}

function CompletionScreen({ session, onComplete, lang: _lang }) {
  const { t } = useLanguage();
  const score = session.total_score || 0;
  const maxScore = session.max_score || 1;
  const pct = Math.floor((score / maxScore) * 100);
  const status = session.status;
  const correctCount = (session.answers || []).filter((a) => a.is_correct).length;
  const total = (session.answers || []).length;

  const emoji = status === 'completed' ? (pct >= 70 ? '🎉' : '📚') : '💔';
  const title =
    status === 'completed'
      ? (t('ei.completion.interview_complete') || 'Interview Complete!')
      : (t('ei.completion.out_of_lives') || 'Out of Lives');

  return (
    <div
      className="bg-white rounded-2xl p-6 border-2 border-gray-200 text-center"
      data-testid="ei-completion-screen"
    >
      <div className="text-6xl mb-3">{emoji}</div>
      <h2 className="text-xl font-bold mb-2">{title}</h2>
      <div className="text-3xl font-bold text-blue-700 mb-2" data-testid="ei-final-score">
        {score} / {maxScore}
      </div>
      <div className="text-sm text-[var(--text-muted)] mb-4">%{pct}</div>

      {total > 0 && (
        <div className="text-left bg-gray-50 rounded-xl p-3 text-xs mb-4">
          <strong>{t('ei.completion.summary') || 'Summary'}:</strong>
          <div>
            {(t('ei.completion.correct_count') || '{correct} / {total} correct')
              .replace('{correct}', correctCount)
              .replace('{total}', total)}
          </div>
        </div>
      )}

      <button onClick={onComplete} className="btn-primary px-8 py-3" data-testid="ei-finish-btn">
        {t('ei.completion.finish') || 'Finish'}
      </button>
    </div>
  );
}
